import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Card, CardContent, Typography, Chip, Alert,
  CircularProgress, LinearProgress, Button, Tooltip, Skeleton,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, TrendingFlat, Refresh as RefreshIcon,
  CheckCircle, ShowChart, Calculate,
} from '@mui/icons-material';
import { XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine } from 'recharts';
import { marketRecoveryAPI } from '../services/api';

// ============================================================
// Shared styles
// ============================================================
const cardSx = {
  height: '100%',
  borderRadius: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
    transform: 'translateY(-1px)',
  },
};

const glassOverlay = {
  bgcolor: 'rgba(255,255,255,0.12)',
  backdropFilter: 'blur(4px)',
  borderRadius: 1,
};

// ============================================================
// Sub-components
// ============================================================

const ScoreCard = ({ data, t }) => {
  if (!data) return null;
  const score = data.recovery_score || 0;
  const signals = data.signals || {};

  const spotSignals = ['no_new_low_10', 'flush_reject', 'atr_contract', 'higher_low'];
  const momSignals = ['rsi_recovering', 'above_ema20', 'strong_return', 'volume_confirm', 'macd_improving'];
  const derivSignals = ['basis_improve_2', 'basis_improve_1', 'short_cover', 'long_build', 'oi_shock_cover', 'basis_near_zero'];
  const spotWeights = { no_new_low_10: 10, flush_reject: 8, atr_contract: 5, higher_low: 7 };
  const momWeights = { rsi_recovering: 8, above_ema20: 7, strong_return: 5, volume_confirm: 5, macd_improving: 4 };
  const derivWeights = { basis_improve_2: 12, basis_improve_1: 6, short_cover: 7, long_build: 10, oi_shock_cover: 3, basis_near_zero: 3 };

  const spotScore = spotSignals.reduce((sum, s) => sum + (signals[s] ? spotWeights[s] : 0), 0);
  const momScore = momSignals.reduce((sum, s) => sum + (signals[s] ? momWeights[s] : 0), 0)
    + Math.min((signals.consecutive_up || 0), 3) * 2; // up to 6 pts
  const derivScore = derivSignals.reduce((sum, s) => sum + (signals[s] ? derivWeights[s] : 0), 0);

  // D. Rally strength bonus (max 20 pts)
  const rallyPct = signals.rally_from_low || 0;
  const volFactor = signals.vol_weak_rally ? 0.5 : 1.0;
  let rallyScore = 0;
  if (rallyPct >= 15) rallyScore += Math.floor(8 * volFactor);
  else if (rallyPct >= 10) rallyScore += Math.floor(5 * volFactor);
  else if (rallyPct >= 5) rallyScore += Math.floor(2 * volFactor);
  if (signals.above_ema50) rallyScore += Math.floor(5 * volFactor);
  if (signals.rsi_bullish) rallyScore += 3;
  if (signals.ema20_above_ema50) rallyScore += 4;

  const boolSignals = Object.entries(signals).filter(([k, v]) => typeof v === 'boolean' && v);
  const totalActive = boolSignals.length;
  const totalSignals = Object.entries(signals).filter(([k, v]) => typeof v === 'boolean').length;

  const getGradient = () => {
    if (score >= 60) return 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)';
    if (score >= 40) return 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)';
    return 'linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%)';
  };

  return (
    <Card sx={{ ...cardSx, background: getGradient(), '&:hover': { ...cardSx['&:hover'], boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}>
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem' }}>{t('marketRecovery.score.title')}</Typography>
          <Chip label={data.trade_date} size="small" sx={{ ...glassOverlay, color: 'white', fontWeight: 600, fontSize: '0.68rem', height: 22 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '2.4rem', lineHeight: 1.1 }}>{score}</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '1rem' }}>/100</Typography>
        </Box>
        <LinearProgress
          variant="determinate" value={score}
          sx={{ my: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white', borderRadius: 3 } }}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.4, mt: 'auto' }}>
          {[
            { label: t('marketRecovery.score.spot'), val: spotScore, max: 30 },
            { label: t('marketRecovery.score.momentum', 'Momentum'), val: momScore, max: 35 },
            { label: t('marketRecovery.score.deriv'), val: derivScore, max: 35 },
            { label: t('marketRecovery.score.rally', 'Rally'), val: rallyScore, max: 20 },
            { label: t('marketRecovery.score.signals'), val: totalActive, max: totalSignals },
          ].map(item => (
            <Box key={item.label} sx={{ p: 0.4, ...glassOverlay, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: '0.48rem', letterSpacing: '0.3px', lineHeight: 1.2 }}>{item.label}</Typography>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.2 }}>
                {item.val}<Typography component="span" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55rem' }}>/{item.max}</Typography>
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const PhaseCard = ({ data, t }) => {
  if (!data) return null;
  const phase = data.phase_label || 'neutral';
  const signals = data.signals || {};
  const configs = {
    bear_continuation: { grad: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)', icon: <TrendingDown sx={{ fontSize: 28 }} /> },
    selling_exhaustion: { grad: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)', icon: <TrendingFlat sx={{ fontSize: 28 }} /> },
    short_cover_bounce: { grad: 'linear-gradient(135deg, #ef6c00 0%, #e65100 100%)', icon: <TrendingFlat sx={{ fontSize: 28 }} /> },
    early_reversal: { grad: 'linear-gradient(135deg, #f9a825 0%, #f57f17 100%)', icon: <TrendingUp sx={{ fontSize: 28 }} /> },
    improving_but_need_build: { grad: 'linear-gradient(135deg, #f9a825 0%, #ff8f00 100%)', icon: <TrendingUp sx={{ fontSize: 28 }} /> },
    momentum_recovery: { grad: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)', icon: <TrendingUp sx={{ fontSize: 28 }} /> },
    tentative_recovery: { grad: 'linear-gradient(135deg, #7986cb 0%, #5c6bc0 100%)', icon: <TrendingUp sx={{ fontSize: 28 }} /> },
    accumulation_turn: { grad: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', icon: <CheckCircle sx={{ fontSize: 28 }} /> },
    neutral: { grad: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)', icon: <TrendingFlat sx={{ fontSize: 28 }} /> },
  };
  const cfg = configs[phase] || configs.neutral;

  const aboveEma20 = data.ema20 && data.spot_close ? data.spot_close >= data.ema20 : null;
  const aboveEma50 = data.ema50 && data.spot_close ? data.spot_close >= data.ema50 : null;
  const rallyPct = signals.rally_from_low || 0;
  const consecUp = signals.consecutive_up || 0;

  // Build context items as flat text
  const contextParts = [];
  if (rallyPct > 0) contextParts.push(`↑${rallyPct.toFixed(1)}% ${t('marketRecovery.phase.fromLow', 'từ đáy')}`);
  if (consecUp > 0) contextParts.push(`${consecUp} ${t('marketRecovery.phase.daysUp', 'phiên tăng')}`);

  return (
    <Card sx={{ ...cardSx, background: cfg.grad, '&:hover': { ...cardSx['&:hover'], boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}>
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem', mb: 0.75 }}>{t('marketRecovery.phase.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white', mb: 0.75 }}>
          {cfg.icon}
          <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.2 }}>{t(`marketRecovery.phase.${phase}`)}</Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem', lineHeight: 1.5, mb: 1 }}>{t(`marketRecovery.phase.desc_${phase}`)}</Typography>
        {/* Rally context line */}
        {contextParts.length > 0 && (
          <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.72rem', mb: 1 }}>
            {contextParts.join('  ·  ')}
          </Typography>
        )}
        {/* Technical levels — flat text rows, no boxes */}
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {data.ema20 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                {aboveEma20 ? <TrendingUp sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }} /> : <TrendingDown sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }} />}
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.62rem' }}>EMA20</Typography>
              </Box>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.68rem' }}>{data.ema20.toFixed(1)}</Typography>
            </Box>
          )}
          {data.ema50 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                {aboveEma50 ? <TrendingUp sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }} /> : <TrendingDown sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }} />}
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.62rem' }}>EMA50</Typography>
              </Box>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.68rem' }}>{data.ema50.toFixed(1)}</Typography>
            </Box>
          )}
          {data.atr14 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.62rem' }}>ATR</Typography>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.68rem' }}>{data.atr14.toFixed(1)}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const ActionCard = ({ data, t }) => {
  if (!data) return null;
  const action = data.action || 'wait';
  const cfgs = {
    probe_or_add: { color: '#2e7d32', bg: '#e8f5e9' },
    probe_small: { color: '#e65100', bg: '#fff3e0' },
    wait: { color: '#546e7a', bg: '#eceff1' },
  };
  const cfg = cfgs[action] || cfgs.wait;

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1 }}>{t('marketRecovery.action.title')}</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: cfg.color, lineHeight: 1.1, mb: 0.5 }}>{t(`marketRecovery.action.${action}`)}</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.5, flex: 1 }}>{t(`marketRecovery.action.desc_${action}`)}</Typography>
        {data.entry_mode && (
          <Box sx={{ mt: 1, p: 1.25, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.3 }}>{t('marketRecovery.action.entryMode')}</Typography>
            <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'text.primary' }}>{t(`marketRecovery.action.${data.entry_mode}`, data.entry_mode)}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const MarketDataCard = ({ data, t }) => {
  if (!data) return null;
  const basisColor = data.basis >= 0 ? '#2e7d32' : '#c62828';
  const oiColor = data.d_oi == null ? '#9e9e9e' : data.d_oi >= 0 ? '#1565c0' : '#e65100';
  const signals = data.signals || {};

  // Determine OI regime label + color
  const getOiRegime = () => {
    if (signals.long_build) return { label: t('marketRecovery.oiRegime.long_build'), color: '#2e7d32', bg: '#e8f5e9' };
    if (signals.short_cover) return { label: t('marketRecovery.oiRegime.short_cover'), color: '#1565c0', bg: '#e3f2fd' };
    if (signals.short_build) return { label: t('marketRecovery.oiRegime.short_build'), color: '#c62828', bg: '#ffebee' };
    if (signals.long_liq) return { label: t('marketRecovery.oiRegime.long_liq'), color: '#e65100', bg: '#fff3e0' };
    return null;
  };
  const oiRegime = getOiRegime();

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1 }}>{t('marketRecovery.marketData.title')}</Typography>
        {/* Prices */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <Box>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.6rem', fontWeight: 600 }}>VN30 Spot</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#1565c0', lineHeight: 1.2 }}>{data.spot_close?.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.6rem', fontWeight: 600 }}>VN30F1M</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#6a1b9a', lineHeight: 1.2 }}>{data.f1_close?.toFixed(2)}</Typography>
          </Box>
        </Box>
        {/* Key metrics — clean row list */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>{t('marketRecovery.marketData.basis')}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: basisColor }}>
              {data.basis?.toFixed(2)}
              <Typography component="span" sx={{ fontSize: '0.62rem', color: 'text.secondary', ml: 0.5 }}>({data.basis_pct?.toFixed(2)}%)</Typography>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>{t('marketRecovery.marketData.oiChange')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: oiColor }}>
                {data.d_oi != null ? `${data.d_oi >= 0 ? '+' : ''}${data.d_oi?.toFixed(0)}` : 'N/A'}
              </Typography>
              {oiRegime && (
                <Chip label={oiRegime.label} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, color: oiRegime.color, bgcolor: oiRegime.bg, border: 'none', '& .MuiChip-label': { px: 0.6 } }} />
              )}
            </Box>
          </Box>
          {data.atr14 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>ATR14</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary' }}>{data.atr14.toFixed(1)}</Typography>
              </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const HistoryChart = ({ data, t }) => {
  if (!data || data.length === 0) {
    return (
      <Card sx={{ ...cardSx, height: 300 }}>
        <CardContent><Typography variant="h6" color="text.secondary">{t('marketRecovery.noData')}</Typography></CardContent>
      </Card>
    );
  }
  const chartData = [...data].reverse().map((d, i, arr) => ({
    date: d.trade_date,
    score: d.recovery_score,
    phase: d.phase_label,
    action: d.action,
    delta: i > 0 ? d.recovery_score - arr[i - 1].recovery_score : 0,
  }));

  return (
    <Card sx={{ ...cardSx, height: '100%' }}>
      <CardContent sx={{ height: '100%', p: 2.5, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 1 }}>{t('marketRecovery.chart.title')}</Typography>
        <Box sx={{ flex: 1, minHeight: 230 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#667eea" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#667eea" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#999' }} tickLine={false} axisLine={{ stroke: '#e0e0e0' }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#999' }} tickLine={false} axisLine={false} />
            <ReferenceLine y={60} stroke="#4caf50" strokeDasharray="4 4" strokeOpacity={0.5} />
            <ReferenceLine y={40} stroke="#ff9800" strokeDasharray="4 4" strokeOpacity={0.5} />
            <RTooltip
              wrapperStyle={{ zIndex: 10 }}
              cursor={{ stroke: '#667eea', strokeWidth: 1, strokeDasharray: '4 4' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const d = payload[0].payload;
                  const scoreColor = d.score >= 60 ? '#2e7d32' : d.score >= 40 ? '#e65100' : '#546e7a';
                  const deltaColor = d.delta > 0 ? '#2e7d32' : d.delta < 0 ? '#c62828' : '#999';
                  const deltaSign = d.delta > 0 ? '+' : '';
                  return (
                    <Box sx={{ minWidth: 170, borderRadius: 2, bgcolor: '#ffffff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid #eee', overflow: 'hidden' }}>
                      <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 500 }}>{d.date}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: scoreColor, fontWeight: 800 }}>{d.score}/100</Typography>
                          {d.delta !== 0 && <Typography sx={{ fontSize: '0.65rem', color: deltaColor, fontWeight: 700 }}>{deltaSign}{d.delta}</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ px: 1.5, py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{t('marketRecovery.chart.tooltipPhase')}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{t(`marketRecovery.phase.${d.phase}`)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{t('marketRecovery.chart.tooltipAction')}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{t(`marketRecovery.action.${d.action}`)}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Area type="monotone" dataKey="score" stroke="#667eea" fill="url(#colorScore)" strokeWidth={2.5} dot={{ r: 3, fill: '#667eea', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#667eea', stroke: 'white', strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const StatsCard = ({ stats, t }) => {
  if (!stats) return null;

  const phaseColors = {
    bear_continuation: '#d32f2f', selling_exhaustion: '#e65100', short_cover_bounce: '#ef6c00',
    early_reversal: '#f9a825', improving_but_need_build: '#ff8f00',
    momentum_recovery: '#43a047', tentative_recovery: '#5c6bc0',
    accumulation_turn: '#2e7d32', neutral: '#546e7a',
  };
  const actionColors = { probe_or_add: '#2e7d32', probe_small: '#ef6c00', wait: '#546e7a' };

  const total = stats.total_signals || 1;
  const phases = stats.phases || [];
  const actions = stats.actions || {};

  return (
    <Card sx={{ ...cardSx, height: '100%' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{t('marketRecovery.stats.title')}</Typography>
          <Chip label={`${stats.total_signals} ${t('marketRecovery.stats.days')}`} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22, bgcolor: '#f5f5f5' }} />
        </Box>

        {/* Average Score */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>{t('marketRecovery.stats.avgScore')}</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: stats.avg_score >= 40 ? '#2e7d32' : 'text.primary', lineHeight: 1 }}>{stats.avg_score}</Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={stats.avg_score || 0}
            sx={{ height: 5, borderRadius: 3, bgcolor: '#e8e8e8', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #667eea, #764ba2)', borderRadius: 3 } }}
          />
        </Box>

        {/* Phase Distribution */}
        <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.5px', mb: 0.75 }}>{t('marketRecovery.stats.phaseDist')}</Typography>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', height: 16, borderRadius: 2, overflow: 'hidden', mb: 0.75 }}>
            {phases.map(p => (
              <Tooltip key={p.phase_label} title={`${t(`marketRecovery.phase.${p.phase_label}`)}: ${p.count} ${t('marketRecovery.stats.days')} (avg ${p.avg_score})`} arrow>
                <Box sx={{ width: `${(p.count / total) * 100}%`, bgcolor: phaseColors[p.phase_label] || '#999', minWidth: p.count > 0 ? 6 : 0, transition: 'width 0.3s' }} />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {phases.map(p => (
              <Box key={p.phase_label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: phaseColors[p.phase_label] || '#999' }} />
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{t(`marketRecovery.phase.${p.phase_label}`)} ({p.count})</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Action Distribution */}
        <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.5px', mb: 0.75 }}>{t('marketRecovery.stats.actionDist')}</Typography>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
          {[
            { key: 'wait', label: t('marketRecovery.stats.wait') },
            { key: 'probe_small', label: t('marketRecovery.stats.probeSmall') },
            { key: 'probe_or_add', label: t('marketRecovery.stats.probeAdd') },
          ].map(({ key, label }) => {
            const count = actions[key] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            const color = actionColors[key];
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: count > 0 ? 1 : 0.65 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.68rem', width: 80, flexShrink: 0, color: count > 0 ? color : 'text.disabled' }}>{label}</Typography>
                <Box sx={{ flex: 1, height: 14, bgcolor: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ width: `${Math.max(pct, count > 0 ? 8 : 0)}%`, height: '100%', bgcolor: color, borderRadius: 2, transition: 'width 0.4s ease' }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', width: 20, textAlign: 'right', color: count > 0 ? 'text.primary' : 'text.disabled' }}>{count}</Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

const SignalsCard = ({ signals, t }) => {
  if (!signals) return null;

  const spotSignals = [
    { key: 'no_new_low_10', pts: 10 },
    { key: 'flush_reject', pts: 8 },
    { key: 'atr_contract', pts: 5 },
    { key: 'higher_low', pts: 7 },
  ];
  const momSignals = [
    { key: 'rsi_recovering', pts: 8 },
    { key: 'above_ema20', pts: 7 },
    { key: 'strong_return', pts: 5 },
    { key: 'consecutive_up', pts: 6, isNumeric: true },
    { key: 'volume_confirm', pts: 5 },
    { key: 'macd_improving', pts: 4 },
  ];
  const derivSignals = [
    { key: 'basis_improve_2', pts: 12 },
    { key: 'basis_improve_1', pts: 6 },
    { key: 'short_cover', pts: 7 },
    { key: 'long_build', pts: 10 },
    { key: 'oi_shock_cover', pts: 3 },
    { key: 'basis_near_zero', pts: 3 },
  ];
  const rallySignals = [
    { key: 'rally_from_low', pts: 8, isRally: true },
    { key: 'above_ema50', pts: 5 },
    { key: 'rsi_bullish', pts: 3 },
    { key: 'ema20_above_ema50', pts: 4 },
    { key: 'vol_weak_rally', pts: 0, isWarning: true },
  ];

  const allBoolSignals = [...spotSignals, ...momSignals, ...derivSignals, ...rallySignals];
  const totalActive = allBoolSignals.filter(s => {
    if (s.isWarning) return false; // warnings don't count as positive signals
    if (s.isNumeric) return (signals[s.key] || 0) > 0;
    if (s.isRally) return (signals[s.key] || 0) >= 5;
    return signals[s.key];
  }).length;
  const totalAll = allBoolSignals.filter(s => !s.isWarning).length;

  const renderGroup = (titleKey, items, maxPts, color = '#667eea') => {
    const groupPts = items.reduce((sum, s) => {
      if (s.isNumeric) return sum + Math.min((signals[s.key] || 0), 3) * 2;
      if (s.isRally) {
        const pct = signals[s.key] || 0;
        if (pct >= 15) return sum + 8;
        if (pct >= 10) return sum + 5;
        if (pct >= 5) return sum + 2;
        return sum;
      }
      return sum + (signals[s.key] ? s.pts : 0);
    }, 0);
    return (
      <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '2px solid', borderColor: color + '33' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color }}>{t(titleKey)}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: groupPts > 0 ? color : 'text.disabled' }}>{groupPts}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>/{maxPts} pts</Typography>
          </Box>
        </Box>
        {items.map(s => {
          const on = s.isWarning ? signals[s.key]
            : s.isNumeric ? (signals[s.key] || 0) > 0
            : s.isRally ? (signals[s.key] || 0) >= 5
            : signals[s.key];
          const displayVal = s.isWarning ? (on ? '⚠' : '—')
            : s.isNumeric ? `${Math.min((signals[s.key] || 0), 3)}×2`
            : s.isRally ? `${(signals[s.key] || 0).toFixed(1)}%`
            : `+${s.pts}`;
          const isNeg = s.isWarning && on;
          return (
            <Box key={s.key} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, py: 0.85, px: 1.5, mb: 0.5,
              borderRadius: 1.5, transition: 'all 0.2s ease',
              bgcolor: isNeg ? '#ffebee' : on ? '#f1f8e9' : 'transparent',
              border: '1px solid', borderColor: isNeg ? '#ef9a9a' : on ? '#c5e1a5' : '#f0f0f0',
              opacity: on ? 1 : 0.7,
            }}>
              {isNeg
                ? <TrendingDown sx={{ fontSize: 18, color: '#c62828', flexShrink: 0 }} />
                : on
                ? <CheckCircle sx={{ fontSize: 18, color: '#4caf50', flexShrink: 0 }} />
                : <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #e0e0e0', flexShrink: 0 }} />
              }
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: on ? 700 : 400, fontSize: '0.8rem', lineHeight: 1.2, color: isNeg ? '#c62828' : on ? 'text.primary' : 'text.secondary' }}>
                  {t(`marketRecovery.signals.${s.key}`)}
                  {s.isNumeric && on && <Typography component="span" sx={{ fontSize: '0.72rem', color: '#2e7d32', ml: 0.5 }}>({signals[s.key]} {t('marketRecovery.phase.daysUp', 'ngày')})</Typography>}
                  {s.isRally && on && <Typography component="span" sx={{ fontSize: '0.72rem', color: '#2e7d32', ml: 0.5 }}>({(signals[s.key] || 0).toFixed(1)}%)</Typography>}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.3 }}>{t(`marketRecovery.signals.desc_${s.key}`)}</Typography>
              </Box>
              <Box sx={{ px: 0.75, py: 0.25, borderRadius: 1, bgcolor: isNeg ? '#ffcdd2' : on ? '#e8f5e9' : '#fafafa' }}>
                <Typography sx={{ fontWeight: 700, color: isNeg ? '#c62828' : on ? '#2e7d32' : '#ccc', fontSize: '0.68rem' }}>{displayVal}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{t('marketRecovery.signals.title')}</Typography>
          <Chip
            label={`${totalActive}/${totalAll} ${t('marketRecovery.signals.active')}`}
            size="small"
            sx={{
              fontWeight: 700, fontSize: '0.7rem', height: 24,
              bgcolor: totalActive >= 8 ? '#e8f5e9' : totalActive >= 5 ? '#fff3e0' : '#f5f5f5',
              color: totalActive >= 8 ? '#2e7d32' : totalActive >= 5 ? '#e65100' : 'text.secondary',
              border: '1px solid',
              borderColor: totalActive >= 8 ? '#a5d6a7' : totalActive >= 5 ? '#ffcc80' : '#e0e0e0',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {renderGroup('marketRecovery.signals.spotTitle', spotSignals, 30, '#1565c0')}
          {renderGroup('marketRecovery.signals.momentumTitle', momSignals, 35, '#6a1b9a')}
          {renderGroup('marketRecovery.signals.derivTitle', derivSignals, 35, '#e65100')}
          {renderGroup('marketRecovery.signals.rallyTitle', rallySignals, 20, '#2e7d32')}
        </Box>
      </CardContent>
    </Card>
  );
};

const RiskNoteCard = ({ data, t }) => {
  if (!data?.risk_note) return null;
  return (
    <Alert
      severity="warning"
      variant="outlined"
      sx={{ borderRadius: 2, borderColor: '#ffcc80', bgcolor: '#fff8e1', '& .MuiAlert-icon': { color: '#e65100' } }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.25 }}>{t('marketRecovery.riskNote')}</Typography>
      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>{t(`marketRecovery.action.${data.risk_note}`, data.risk_note)}</Typography>
    </Alert>
  );
};

// ============================================================
// Main Page
// ============================================================

function MarketRecovery() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [latestSignal, setLatestSignal] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Helper: check if a date string is today
  const isToday = useCallback((dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return dateStr === `${yyyy}-${mm}-${dd}`;
  }, []);

  // Soft refresh: update data without full skeleton loading
  const refreshData = useCallback(async () => {
    setError(null);
    try {
      const [latestRes, historyRes, statsRes] = await Promise.all([
        marketRecoveryAPI.getLatest(),
        marketRecoveryAPI.getHistory(30),
        marketRecoveryAPI.getStats(30),
      ]);
      setLatestSignal(latestRes.data);
      setHistory(historyRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error refreshing:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    }
  }, []);

  // Calculate and update: calls POST then uses response directly
  const calculateAndUpdate = useCallback(async () => {
    setCalculating(true);
    setError(null);
    try {
      const calcRes = await marketRecoveryAPI.calculate({
        index_code: 'VN30',
        futures_symbol: 'VN30F1M',
        trading_days: 90,
      });
      // Use the calculation result directly for latest signal
      if (calcRes.data?.result) {
        setLatestSignal(calcRes.data.result);
      }
      // Refresh history & stats in background (no full reload)
      const [historyRes, statsRes] = await Promise.all([
        marketRecoveryAPI.getHistory(30),
        marketRecoveryAPI.getStats(30),
      ]);
      setHistory(historyRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Calculate error:', err);
      setError(err.response?.data?.detail || 'Failed to calculate');
    } finally {
      setCalculating(false);
    }
  }, []);

  // Initial load: fetch data → if stale, auto-calculate
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [latestRes, historyRes, statsRes] = await Promise.all([
        marketRecoveryAPI.getLatest(),
        marketRecoveryAPI.getHistory(30),
        marketRecoveryAPI.getStats(30),
      ]);
      setLatestSignal(latestRes.data);
      setHistory(historyRes.data);
      setStats(statsRes.data);
      setLoading(false);

      // Auto-calculate if latest data is NOT from today
      const latestDate = latestRes.data?.trade_date;
      if (!isToday(latestDate)) {
        // Data is stale → auto-calculate with latest market data
        await calculateAndUpdate();
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
      setLoading(false);
    }
  }, [calculateAndUpdate, isToday]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(refreshData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData, refreshData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshData();
    setRefreshing(false);
  };

  const handleCalculate = async () => {
    await calculateAndUpdate();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        {/* ── Header skeleton ── */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 1.5 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton width={220} height={24} />
                <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 3 }} />
                <Skeleton width={180} height={14} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={110} height={36} sx={{ borderRadius: '4px' }} />
            <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: '4px' }} />
          </Box>
        </Box>

        {/* ── Risk Note banner skeleton ── */}
        <Box sx={{ mb: 2.5 }}>
          <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
        </Box>

        {/* ── 4 Main Stats Cards ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          {/* ScoreCard */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={{ ...cardSx, background: 'linear-gradient(135deg, #b0bec5 0%, #90a4ae 100%)' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Skeleton width={80} height={12} sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                  <Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Skeleton width={70} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Skeleton width={30} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
                </Box>
                <Skeleton variant="rounded" height={6} sx={{ my: 1.5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{ flex: 1, p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.12)' }}>
                      <Skeleton width={30} height={10} sx={{ mb: 0.3, bgcolor: 'rgba(255,255,255,0.2)' }} />
                      <Skeleton width={40} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* PhaseCard */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={{ ...cardSx, background: 'linear-gradient(135deg, #b0bec5 0%, #90a4ae 100%)' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Skeleton width={60} height={12} sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.25)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Skeleton width={130} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                </Box>
                <Skeleton width="90%" height={12} sx={{ mb: 0.5, bgcolor: 'rgba(255,255,255,0.15)' }} />
                <Skeleton width="70%" height={12} sx={{ mb: 1.5, bgcolor: 'rgba(255,255,255,0.15)' }} />
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {['EMA20', 'EMA50', 'ATR'].map(l => (
                    <Skeleton key={l} variant="rounded" width={52} height={22} sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.12)' }} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* ActionCard */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Skeleton width={70} height={12} sx={{ mb: 1.5 }} />
                <Skeleton width={140} height={28} sx={{ mb: 0.5 }} />
                <Skeleton width="95%" height={12} sx={{ mb: 0.3 }} />
                <Skeleton width="80%" height={12} sx={{ mb: 1.5 }} />
                <Box sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
                  <Skeleton width={70} height={10} sx={{ mb: 0.3 }} />
                  <Skeleton width="90%" height={12} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* MarketDataCard */}
          <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Skeleton width={80} height={12} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  {[0, 1].map(i => (
                    <Box key={i} sx={{ flex: 1 }}>
                      <Skeleton width={60} height={10} sx={{ mb: 0.3 }} />
                      <Skeleton width={80} height={26} />
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {[0, 1].map(i => (
                    <Box key={i} sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#f5f5f5' }}>
                      <Skeleton width={40} height={10} sx={{ mb: 0.3 }} />
                      <Skeleton width={70} height={18} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* ── Charts Row ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* HistoryChart */}
          <Box sx={{ flex: '2 1 0', minWidth: 400 }}>
            <Card sx={{ ...cardSx, height: '100%', minHeight: 300 }}>
              <CardContent sx={{ height: '100%', p: 2.5, display: 'flex', flexDirection: 'column' }}>
                <Skeleton width={180} height={18} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 1.5 }} />
              </CardContent>
            </Card>
          </Box>

          {/* StatsCard */}
          <Box sx={{ flex: '1 1 0', minWidth: 300 }}>
            <Card sx={{ ...cardSx, height: '100%', minHeight: 300 }}>
              <CardContent sx={{ height: '100%', p: 2.5, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Skeleton width={100} height={18} />
                  <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: 3 }} />
                </Box>
                {/* Avg score */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Skeleton width={70} height={12} />
                    <Skeleton width={40} height={26} />
                  </Box>
                  <Skeleton variant="rounded" height={5} sx={{ borderRadius: 3 }} />
                </Box>
                {/* Phase distribution */}
                <Skeleton width={100} height={10} sx={{ mb: 0.75 }} />
                <Skeleton variant="rounded" height={16} sx={{ mb: 0.75, borderRadius: 2 }} />
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Skeleton variant="circular" width={7} height={7} />
                      <Skeleton width={55} height={10} />
                    </Box>
                  ))}
                </Box>
                {/* Action distribution */}
                <Skeleton width={100} height={10} sx={{ mb: 0.75 }} />
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Skeleton width={80} height={12} sx={{ flexShrink: 0 }} />
                    <Skeleton variant="rounded" height={14} sx={{ flex: 1, borderRadius: 2 }} />
                    <Skeleton width={16} height={12} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* ── Signals Card ── */}
        <Card sx={{ ...cardSx, mb: 2.5 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Skeleton width={160} height={20} />
              <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {/* Spot signals group */}
              {[4, 5].map((count, gi) => (
                <Box key={gi} sx={{ flex: '1 1 0', minWidth: 280 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Skeleton width={100} height={16} />
                    <Skeleton width={50} height={14} />
                  </Box>
                  {Array.from({ length: count }).map((_, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.85, px: 1.5, mb: 0.5, borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
                      <Skeleton variant="circular" width={18} height={18} sx={{ flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="75%" height={14} sx={{ mb: 0.3 }} />
                        <Skeleton width="90%" height={10} />
                      </Box>
                      <Skeleton variant="rounded" width={28} height={18} sx={{ borderRadius: 1 }} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* ── Footer ── */}
        <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #f0f0f0', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {[0, 1, 2].map(i => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={8} height={8} />
              <Skeleton width={140} height={12} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}><Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert></Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER — Consistent with other pages
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(102, 126, 234, 0.3)',
          }}>
            <ShowChart sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('marketRecovery.title')}
              </Typography>
              {latestSignal?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#667eea', boxShadow: '0 0 6px #667eea' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {latestSignal.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('marketRecovery.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Right: Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Calculate />}
            onClick={handleCalculate}
            disabled={calculating}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#667eea', '&:hover': { bgcolor: '#5568d3' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {calculating ? t('marketRecovery.calculating', 'Đang tính...') : t('marketRecovery.calculateNow', 'Tính ngay')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {refreshing ? t('marketRecovery.refreshing') : t('marketRecovery.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Calculating Banner */}
      {calculating && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-icon': { alignItems: 'center' } }}
          icon={<CircularProgress size={18} />}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            {t('marketRecovery.calculatingBanner', 'Đang tính toán với dữ liệu mới nhất...')}
          </Typography>
        </Alert>
      )}

      {/* Risk Note Banner */}
      {latestSignal?.risk_note && (
        <Box sx={{ mb: 2.5 }}>
          <RiskNoteCard data={latestSignal} t={t} />
        </Box>
      )}

      {/* Main Stats Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><ScoreCard data={latestSignal} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><PhaseCard data={latestSignal} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><ActionCard data={latestSignal} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220 }}><MarketDataCard data={latestSignal} t={t} /></Box>
      </Box>

      {/* Charts Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Box sx={{ flex: '2 1 0', minWidth: 400 }}><HistoryChart data={history} t={t} /></Box>
        <Box sx={{ flex: '1 1 0', minWidth: 300 }}><StatsCard stats={stats} t={t} /></Box>
      </Box>

      {/* Signals Row */}
      {latestSignal?.signals && (
        <Box sx={{ mb: 2.5 }}>
          <SignalsCard signals={latestSignal.signals} t={t} />
        </Box>
      )}

      {/* Footer info */}
      <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #f0f0f0', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Score ≥ 60', desc: t('marketRecovery.footer.strong'), color: '#2e7d32' },
          { label: 'Score 40-59', desc: t('marketRecovery.footer.bounce'), color: '#ef6c00' },
          { label: 'Score < 40', desc: t('marketRecovery.footer.noSignal'), color: '#546e7a' },
        ].map(item => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
            <Typography sx={{ fontSize: '0.75rem' }}><Typography component="span" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{item.label}</Typography> — {item.desc}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default MarketRecovery;
